import { Stack, router } from 'expo-router';
import * as React from 'react';
import { ActivityIndicator, ScrollView, View } from 'react-native';

import { Button } from '@sixam/ui/button';
import { Text } from '@sixam/ui/text';
import { authedFetch } from '@/lib/authed-fetch';
import { TERMS_SUMMARY } from '@sixam/shared/community-terms';
import { unblockMember } from '@/lib/moderation';

type BlockedMember = Readonly<{ fullName: string | null; id: string }>;

/**
 * Safety, in one place a member can always reach.
 *
 * Deliberately not behind a release flag. Everything else in settings can be
 * switched off for a release; the controls App Review requires cannot be, or
 * they are unreachable exactly when someone needs them.
 */
export default function SafetySettingsScreen() {
  const [blocked, setBlocked] = React.useState<BlockedMember[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    let active = true;

    async function load() {
      setIsLoading(true);
      try {
        const response = await authedFetch('/api/moderation/blocks?withProfiles=1');
        const payload = (await response.json()) as {
          blocked?: BlockedMember[];
          error?: string;
        };
        if (!response.ok) throw new Error(payload.error ?? 'Could not load blocked members.');
        if (active) {
          setBlocked(payload.blocked ?? []);
          setError(null);
        }
      } catch (loadError) {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'Could not load.');
        }
      } finally {
        if (active) setIsLoading(false);
      }
    }

    void load();
    return () => {
      active = false;
    };
  }, [reloadToken]);

  async function unblock(member: BlockedMember) {
    try {
      await unblockMember(member.id);
      setBlocked((current) => current.filter((row) => row.id !== member.id));
    } catch (unblockError) {
      setError(unblockError instanceof Error ? unblockError.message : 'Could not unblock.');
    }
  }

  return (
    <>
      <Stack.Screen options={{ headerShown: true, title: 'Safety' }} />
      <ScrollView
        className="flex-1 bg-background"
        contentContainerClassName="gap-6 p-6"
        contentInsetAdjustmentBehavior="automatic"
      >
        <View className="gap-2">
          <Text variant="large">Community standards</Text>
          <Text variant="muted">{TERMS_SUMMARY}</Text>
          <Button onPress={() => router.push('/terms')} variant="secondary">
            <Text>Read the community terms</Text>
          </Button>
        </View>

        <View className="gap-2 border-t border-border pt-6">
          <Text variant="large">How to report someone</Text>
          <Text variant="muted">
            Tap any runner in an event&apos;s attendee list, or open an event and choose Report this
            event. You can report content or block the member from the same place.
          </Text>
        </View>

        <View className="gap-3 border-t border-border pt-6">
          <Text variant="large">Blocked members</Text>
          {isLoading ? <ActivityIndicator /> : null}
          {error ? (
            <Text className="text-sm text-destructive" selectable>
              {error}
            </Text>
          ) : null}
          {!isLoading && blocked.length === 0 ? (
            <Text variant="muted">You have not blocked anyone.</Text>
          ) : null}
          {blocked.map((member) => (
            <View
              className="flex-row items-center justify-between gap-3 border-b border-border py-3"
              key={member.id}
            >
              <Text className="min-w-0 flex-1" numberOfLines={1}>
                {member.fullName ?? '6am member'}
              </Text>
              <Button onPress={() => void unblock(member)} size="sm" variant="secondary">
                <Text>Unblock</Text>
              </Button>
            </View>
          ))}
          <Button onPress={() => setReloadToken((token) => token + 1)} variant="ghost">
            <Text>Refresh</Text>
          </Button>
        </View>
      </ScrollView>
    </>
  );
}
